import { StudiesApiService } from "../api/StudiesService";
import { CreatedOrderDetails, IStudySubject } from "../models/order.interface";
import {
  AddStudyFailureReason,
  IAddStudyResult,
} from "../models/results/add-study-result.interface";
import { IResult } from "../models/results/result.interface";
import Role from "../models/role";
import {
  AddStudy,
  IOrganizationStudies,
  IStudy,
  UpdateStudy,
} from "../models/study.interface";
import { IUser } from "../models/users.interface";
import logger from "../utils/logger";
import { getOrderStatus } from "../utils/orderUtil";
import { getAddStudyFailure } from "../utils/studyUtil";
import { getGenderFromServerGender } from "../utils/userUtil";
import usersService from "./users.service";

const getStudies = async (): Promise<IOrganizationStudies | null> => {
  logger.info(getStudies.name, "Getting studies...");
  try {
    const {
      response: resp,
      error,
    } = await StudiesApiService.studiesGetOrganizationStudies();
    if (error || !resp) {
      logger.error(getStudies.name, "Invalid API response", { resp, error });
      return null;
    }

    const orgStudies: IOrganizationStudies = {
      ...resp,
      studies:
        resp.studies?.map((apiStudy) => {
          const {
            id,
            name,
            users,
            reference,
            isActive,
            totalLinkedUsersCount,
            activelyLinkedUsersCount,
            subjectCount,
            ecgsCount,
            visits,

          } = apiStudy;

          const study: IStudy = {
            id,
            name,
            isActive,
            reference,
            users: users.map((u) => ({
              id: u.id,
              name: u.name,
              isLinked: u.isLinkActive,
              canApproveEcgs: u.canApproveEcgs,
            })),
            totalLinkedUsers: totalLinkedUsersCount,
            totalActiveLinkedUsers: activelyLinkedUsersCount,
            totalSubjects: subjectCount,
            visits: visits.map((v) => ({
              id: v.id ?? "",
              name: v.name ?? "",
              hasOrders: v.hasOrders,
            })),
            totalEcgs: ecgsCount,
          };
          return study;
        }) ?? [],
    };

    return orgStudies;
  } catch (error) {
    logger.error(getStudies.name, "Error getting studies.", error);
    return null;
  }
};

const addStudy = async (study: AddStudy): Promise<IAddStudyResult> => {
  logger.info(addStudy.name, `Adding new study`, study);

  const { name, reference, visits } = study;

  try {
    const {
      response: resp,
      error,
    } = await StudiesApiService.studiesCreateStudy({
      studyDto: {
        name,
        reference,
        visits: visits ?? [],
      },
    });

    if (error || !resp) {
      logger.error(addStudy.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
        failReason: getAddStudyFailure(error?.errorCode?.toString() ?? ""),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(addStudy.name, "Error adding study.", error);
    return {
      success: false,
      message: (error as Error).message,
      failReason: AddStudyFailureReason.Unknown,
    };
  }
};

const updateStudy = async (
  study: UpdateStudy
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(updateStudy.name, `Updating study`, study);

  const { id, name, reference, visits } = study;

  try {
    const {
      response: resp,
      error,
    } = await StudiesApiService.studiesUpdateStudy({
      id,
      studyDto: {
        name,
        reference,
        visits,
      },
    });

    if (error || !resp) {
      logger.error(updateStudy.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(updateStudy.name, "Error updating study.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const toggleUsersLinkToStudy = async (
  usersIds: string[],
  studyId: string,
  isLinked: boolean
): Promise<Omit<IResult<any>, "data">> => {
  logger.info(
    toggleUsersLinkToStudy.name,
    `${isLinked ? "Linking" : "Unlinking"} users ${JSON.stringify(
      usersIds,
      null,
      2
    )} ${isLinked ? "to" : "from"} study ${studyId}`
  );

  try {
    const {
      response: resp,
      error,
    } = await StudiesApiService.studiesUpdateUsers({
      id: studyId,
      users: usersIds.map((id) => ({ userId: id, isActive: isLinked })),
    });

    if (error || !resp) {
      logger.error(toggleUsersLinkToStudy.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(
      toggleUsersLinkToStudy.name,
      `Error ${isLinked ? "linking" : "unlinking"} users ${
        isLinked ? "to" : "from"
      } study.`,
      error
    );
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const deleteStudy = async (id: string): Promise<Omit<IResult<any>, "data">> => {
  logger.info(deleteStudy.name, `Deleting study ${id}`);

  try {
    const {
      response: resp,
      error,
    } = await StudiesApiService.studiesDeleteStudy({ id });

    if (error || !resp) {
      logger.error(deleteStudy.name, "Invalid API response", { resp, error });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    // Removed successfully
    return {
      success: true,
    };
  } catch (error) {
    logger.error(deleteStudy.name, "Error removing study.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const getStudyAvailableUsers = async (): Promise<IUser[] | null> => {
  logger.info(getStudyAvailableUsers.name, `Getting study linkable users...`);

  try {
    const users = await usersService.getUsers([
      Role.OrgProfessional,
      Role.OrgAdmin,
    ]);

    return users;
  } catch (error) {
    logger.error(
      getStudyAvailableUsers.name,
      "Error getting study linkable users.",
      error
    );
    return null;
  }
};

const createOrder = async (
  studyId: string,
  subjectId: string,
  subjectGender: string,
  subjectYearOfBirth: number,
  visitId: string
): Promise<IResult<CreatedOrderDetails>> => {
  logger.info(
    createOrder.name,
    `Creating an order for subject ${subjectId} in study ${studyId}`
  );

  try {
    const {
      response: resp,
      error,
    } = await StudiesApiService.studiesCreateSubjectOrder({
      id: studyId,
      order: { subjectId, subjectGender, subjectYearOfBirth, visitId },
    });

    if (error || !resp) {
      logger.error(createOrder.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    return {
      success: true,
      data: resp,
    };
  } catch (error) {
    logger.error(updateStudy.name, "Error creating order.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const getOrderByStudySubject = async (
  studyId: string,
  subjectId: string
): Promise<IResult<IStudySubject>> => {
  logger.info(
    getOrderByStudySubject.name,
    `Get order by study ${studyId} and subject ${subjectId}.`
  );

  try {
    const {
      response: resp,
      error,
    } = await StudiesApiService.studiesGetStudySubject({
      studyId,
      subjectId,
    });

    if (error) {
      logger.error(getOrderByStudySubject.name, "Invalid API response", {
        resp,
        error,
      });
      return {
        success: false,
        message: error?.errorMessage,
        errorCode: error?.errorCode?.toString(),
      };
    }

    const orderStatus = getOrderStatus(resp);

    // subject is new in this study
    if (!resp) {
      return {
        success: true,
        data: { orderStatus },
      };
    }

    const subject = {
      id: resp.subjectId,
      gender: !!resp.existsInOtherStudyName
        ? undefined
        : getGenderFromServerGender(resp.gender), // gender retrived with a default value (gender = 0) in case of a subject that already exists in other study
      yearOfBirth: !!resp.existsInOtherStudyName ? undefined : resp.yearOfBirth, // yearOfBirth retrived with a default value (yearOfBirth = 0) in case of a subject that already exists in other study
      nextVisitId: resp.nextVisitId,
      order: resp.order
        ? {
            number: resp.order.orderNumber,
            isCancelable: resp.order.canCancel,
            visitId: resp.order.visitId,
          }
        : undefined,
      otherStudy: {
        isExists: !!resp.existsInOtherStudyName,
        name: resp.existsInOtherStudyName ?? "",
      },
      orderStatus,
    };

    return {
      success: true,
      data: subject,
    };
  } catch (error) {
    logger.error(getOrderByStudySubject.name, "Error getting order.", error);
    return {
      success: false,
      message: (error as Error).message,
    };
  }
};

const studiesService = {
  getStudies,
  addStudy,
  updateStudy,
  toggleUsersLinkToStudy,
  deleteStudy,
  getStudyAvailableUsers,
  createOrder,
  getOrderByStudySubject,
};

export default studiesService;
